// src/pages/InventoryPage.tsx
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Box,
  Snackbar,
  Alert,
  Button,
  Typography,
  Chip,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  SelectChangeEvent,
  Pagination,
} from '@mui/material';
import {
  ListPageScaffold,
  ListPageMain,
  ListPageToolbar,
  ListPageFiltersSection,
} from '../components/common/list-page';
import {
  Close as CloseIcon,
  Summarize as SummarizeIcon,
  CheckCircle as CheckCircleIcon,
  AccessTime as AccessTimeIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import ActionButtons from '../components/inventory/ActionButtons';
import InventoryFilters from '../components/inventory/InventoryFilters';
import InventoryTable from '../components/inventory/InventoryTable/index';
import {
  StyledDialog,
  StyledDialogTitle,
  StyledDialogContent,
  StyledDialogActions,
  CloseButton,
  ModernButton,
  insetPanelPaperSx,
} from '../components/inventory/InventoryTransfer/InventoryTransfer.styles';
import CommonPagination, { PaginationGoToPageField } from '../components/common/Pagination';
import { CancelConfirmationModal } from '../components/modals/confirmation';
import { StatusSummaryBar, type StatusSummaryMetric } from '../components/common/StatusSummaryBar';

import {
  InventoryFilter,
  InventoryRecord,
  InventoryPaginationState,
  WarehouseProduct,
} from '../types/inventory.types';
import {
  INV_STATUS_BRANCH_LABEL,
  INV_STATUS_WAREHOUSE_LABEL,
} from '../constants/inventory-status-labels';
import { AddInventoryPanel } from '../components/inventory/add-panel';
import { InventoryRecordsService, SuppliersService } from '../api';
import { useInventoryRecord } from '../hooks/useInventoryRecord';
import type { InventoryRecordDetailPage } from '../hooks/useInventoryRecord';
import { printInventoryRecord } from '../utils/printInventoryRecord';
import { formatDateTime } from '../utils/formatters/date';
import { formatCurrency } from '../utils/formatters/money';
import { resolvePieceDetailsForItem } from '../utils/inventory/inventoryRecordPieceDetails';
import {
  isInventoryRecordCancelled,
  isInventoryRecordCompleted,
} from '../utils/inventory/inventoryRecordStatus';
import { sortByProductCodeUnique } from '../utils/productCodeSort';
import { INVENTORY_MESSAGES, createErrorNotification } from '../utils/constants';
import { colors } from '../styles/theme/colors';
import EmptyStateMessage from '../components/common/EmptyStateMessage';
import InventoryLabelPrintModal from '../components/inventory/InventoryLabelPrintModal/InventoryLabelPrintModal';
import { useInventoryRegisterPermissions } from '../hooks/useInventoryRegisterPermissions';
import {
  downloadCsvExport,
  csvExportDateSuffix,
  CsvExportError,
} from '../utils/csvExport/downloadCsvExport';

const InventoryPage: React.FC = () => {
  const location = useLocation();

  const emptyInventoryFilters = (): InventoryFilter => ({
    productCode: '',
    productName: '',
    startDate: '',
    endDate: '',
    location: '',
    supplierId: '',
    recordStatus: '',
  });

  // Draft inputs vs applied (chips + fetch) — same pattern as ClientManagement
  const [filters, setFilters] = useState<InventoryFilter>(emptyInventoryFilters);
  const [appliedFilters, setAppliedFilters] = useState<InventoryFilter>(emptyInventoryFilters);

  // Pagination state
  const [pagination, setPagination] = useState<InventoryPaginationState>({
    itemsPerPage: 10,
    currentPage: 1,
    totalItems: 0,
  });
  const [tableSort, setTableSort] = useState<{
    sortBy: 'createdAt' | 'recordCompletedAt' | 'recordCode';
    sortOrder: 'asc' | 'desc';
  }>({
    sortBy: 'createdAt',
    sortOrder: 'desc',
  });

  // Selected records state
  const [selectedRecords, setSelectedRecords] = useState<number[]>([]);

  // Add/Edit panel state
  const [showAddPanel, setShowAddPanel] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentEditRecord, setCurrentEditRecord] = useState<InventoryRecord | null>(null);

  // View record panel state
  const [showViewPanel, setShowViewPanel] = useState(false);
  const [currentViewRecord, setCurrentViewRecord] = useState<InventoryRecordDetailPage | null>(
    null,
  );
  const [isViewItemsLoading, setIsViewItemsLoading] = useState(false);

  // State for expanded products in view panel
  const [expandedProducts, setExpandedProducts] = useState<Set<number>>(new Set());

  // Pagination state for items in view panel (server-side via /detailed-items)
  const [itemsPage, setItemsPage] = useState(1);
  const itemsPerPage = 100;

  // Loading and error states
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isExporting, setIsExporting] = useState<boolean>(false);

  // State to control manual search
  const [shouldSearch, setShouldSearch] = useState(false);

  const [inventoryRecords, setInventoryRecords] = useState<InventoryRecord[]>([]);
  const [suppliersList, setSuppliersList] = useState<Array<{ id: number; name: string }>>([]);

  // Notification state
  const [notification, setNotification] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'info' | 'warning' | 'error';
  }>({
    open: false,
    message: '',
    severity: 'info',
  });

  // Cancel confirmation modal state
  const [cancelModal, setCancelModal] = useState<{
    open: boolean;
    recordId: number | null;
    recordCode: string;
    loading: boolean;
  }>({
    open: false,
    recordId: null,
    recordCode: '',
    loading: false,
  });

  const [labelPrintModal, setLabelPrintModal] = useState<{
    open: boolean;
    recordId: number | null;
    recordCode: string;
  }>({ open: false, recordId: null, recordCode: '' });

  const { canAdd, canExport, canViewRow, canEdit, canCancel, canPrintDetail, canLabelPrint } =
    useInventoryRegisterPermissions();

  const [statusSummary, setStatusSummary] = useState({
    total: 0,
    completed: 0,
    pending: 0,
    cancelled: 0,
  });
  const [statusSummaryLoading, setStatusSummaryLoading] = useState(false);

  const INVENTORY_RECORD_SUMMARY_METRICS: StatusSummaryMetric[] = useMemo(
    () => [
      {
        key: 'total',
        label: 'Total registros',
        icon: <SummarizeIcon />,
        iconVariant: 'inventory',
        filterValue: '',
      },
      {
        key: 'completed',
        label: 'Completados',
        icon: <CheckCircleIcon />,
        iconVariant: 'sales',
        filterValue: 'completed',
      },
      {
        key: 'pending',
        label: 'Pendientes',
        icon: <AccessTimeIcon />,
        iconVariant: 'warning',
        filterValue: 'pending',
      },
      {
        key: 'cancelled',
        label: 'Cancelados',
        icon: <CancelIcon />,
        iconVariant: 'cost',
        filterValue: 'cancelled',
      },
    ],
    [],
  );

  const fetchStatusSummary = useCallback(async () => {
    setStatusSummaryLoading(true);
    try {
      const response = await InventoryRecordsService.inventoryRecordsControllerGetRecordSummary();
      const completed = Number(response?.completed ?? 0);
      const pending = Number(response?.pending ?? 0);
      const cancelled = Number(response?.cancelled ?? 0);
      setStatusSummary({
        total: completed + pending + cancelled,
        completed,
        pending,
        cancelled,
      });
    } catch (error) {
      console.error('Error fetching inventory record status summary:', error);
    } finally {
      setStatusSummaryLoading(false);
    }
  }, []);

  const handleStatusMetricClick = useCallback((filterValue: string) => {
    setFilters((prev) => ({ ...prev, recordStatus: filterValue }));
    setAppliedFilters((prev) => ({ ...prev, recordStatus: filterValue }));
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
    setShouldSearch((prev) => !prev);
  }, []);

  // Custom hook for inventory record details
  const { fetchRecord: fetchInventoryRecordDetails, fetchRecordPage } = useInventoryRecord();

  // Map status to display format
  const mapStatus = (status: string): 'Completado' | 'Pendiente' | 'Cancelado' => {
    switch (status?.toLowerCase()) {
      case 'completed':
        return 'Completado';
      case 'pending':
        return 'Pendiente';
      case 'cancelled':
        return 'Cancelado';
      default:
        return 'Pendiente';
    }
  };

  // Fetch inventory records (always from appliedFilters, not draft inputs)
  const fetchInventory = async () => {
    setIsLoading(true);

    try {
      // Get status in the format expected by the API
      let statusParam: 'COMPLETED' | 'CANCELLED' | 'PENDING' | undefined = undefined;
      if (appliedFilters.recordStatus) {
        const statusLower = appliedFilters.recordStatus.toLowerCase();
        if (statusLower === 'completed' || statusLower === 'completado') {
          statusParam = 'COMPLETED';
        } else if (statusLower === 'cancelled' || statusLower === 'cancelado') {
          statusParam = 'CANCELLED';
        } else if (statusLower === 'pending' || statusLower === 'pendiente') {
          statusParam = 'PENDING';
        }
      }

      // Call the enhanced API with all filter parameters - the backend now handles all filtering
      const response = await InventoryRecordsService.inventoryRecordsControllerFindAll(
        pagination.currentPage, // page
        pagination.itemsPerPage, // limit
        appliedFilters.productCode || undefined, // recordCode
        statusParam, // status
        undefined, // destinationLocationId: no filtro por ubicación (registros van a almacén)
        appliedFilters.startDate || undefined, // startDate - now handled by backend
        appliedFilters.endDate || undefined, // endDate - now handled by backend
        appliedFilters.productName || undefined, // searchTerm - can search across record/product data
        undefined, // createdInLastHours - not used in current UI
        appliedFilters.supplierId ? parseInt(appliedFilters.supplierId, 10) : undefined, // supplierId
        tableSort.sortBy,
        tableSort.sortOrder === 'asc' ? 'ASC' : 'DESC',
      );

      // Process the response
      if (response && response.data) {
        // Transform API response into InventoryRecord[] format
        const formattedRecords = response.data.map((item: any) => {
          const rawStatus = String(item.status || '').toUpperCase();
          const registrationIso = rawStatus === 'COMPLETED' ? item.completedAt : null;
          return {
            id: item.id,
            recordCode: item.recordCode ?? null,
            destinationLocationId: item.destinationLocationId || 0,
            destinationLocation: item.destinationLocation,
            location: item.destinationLocation?.name || 'Sin ubicación',
            date: registrationIso
              ? new Date(registrationIso).toLocaleString('es-ES', {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                })
              : '—',
            createdAt: item.createdAt || undefined,
            completedAt: item.completedAt ?? null,
            totalItemsCount: item.totalItemsCount || 0,
            distinctProductsCount: item.distinctProductsCount || 0,
            status: mapStatus(item.status),
            branch_id: item.destinationLocationId || 0,
            items: item.items || [],
            isDeletable: item.isDeletable || false, // Map the isDeletable property
            supplier: item.supplier
              ? { id: item.supplier.id, name: item.supplier.name }
              : undefined,
          };
        });

        // Set the formatted records to state
        setInventoryRecords(formattedRecords);

        // Update pagination
        setPagination((prev: InventoryPaginationState) => ({
          ...prev,
          totalItems: response.meta?.total || formattedRecords.length,
          currentPage: response.meta?.page || prev.currentPage,
        }));
      } else {
        // Handle empty data
        setInventoryRecords([]);
        setPagination((prev: InventoryPaginationState) => ({
          ...prev,
          totalItems: 0,
        }));
      }
    } catch (error: unknown) {
      // Log full detail for soporte/desarrollo; la UI muestra estado vacío neutro (no alarmar al usuario).
      console.error('Error fetching inventory records:', error);
      setInventoryRecords([]);
      setPagination((prev: InventoryPaginationState) => ({
        ...prev,
        totalItems: 0,
      }));
    } finally {
      setIsLoading(false);
    }
  };

  const fetchSuppliers = async () => {
    try {
      const response = await SuppliersService.suppliersControllerFindAll(
        1,
        500,
        undefined,
        undefined,
        'ACTIVE',
      );
      const raw = (response as { data?: unknown })?.data ?? response;
      if (Array.isArray(raw)) {
        setSuppliersList(
          raw.map((s: { id: number; name: string }) => ({ id: s.id, name: s.name })),
        );
      } else {
        setSuppliersList([]);
      }
    } catch (error: unknown) {
      console.error('Error fetching suppliers:', error);
      setSuppliersList([]);
    }
  };

  // Initial data fetch
  useEffect(() => {
    fetchSuppliers();
    void fetchStatusSummary();
    // Load initial data
    setShouldSearch((prev) => !prev);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch inventory when pagination changes or when search is triggered
  useEffect(() => {
    fetchInventory();
  }, [pagination.currentPage, pagination.itemsPerPage, shouldSearch, tableSort]); // eslint-disable-line react-hooks/exhaustive-deps

  // Deep-link: abrir detalle o filtrar pendientes desde Inicio
  useEffect(() => {
    const state = location.state as {
      openRecordDetails?: number;
      filterRecordStatus?: string;
    } | null;
    if (!state) return;

    if (state.filterRecordStatus) {
      handleStatusMetricClick(state.filterRecordStatus);
    }
    if (state.openRecordDetails) {
      handleViewRecord(state.openRecordDetails);
    }

    if (state.filterRecordStatus || state.openRecordDetails) {
      window.history.replaceState({}, document.title);
    }
  }, [location.state]); // eslint-disable-line react-hooks/exhaustive-deps

  // Event handlers
  const handleSearch = (nextFilters?: InventoryFilter) => {
    setAppliedFilters(nextFilters ?? filters);
    setPagination((prev: InventoryPaginationState) => ({
      ...prev,
      currentPage: 1,
    }));
    setShouldSearch((prev) => !prev);
  };

  const handleReset = () => {
    const empty = emptyInventoryFilters();
    setFilters(empty);
    setAppliedFilters(empty);
    setPagination({
      currentPage: 1,
      itemsPerPage: 10,
      totalItems: 0,
    });
    setShouldSearch((prev) => !prev);
    // Reiniciar también refresca el dashboard (p. ej. al volver a una pestaña ya abierta).
    void fetchStatusSummary();
  };

  const handleClearAppliedField = (field: keyof InventoryFilter) => {
    setFilters((prev) => ({ ...prev, [field]: '' }));
    setAppliedFilters((prev) => ({ ...prev, [field]: '' }));
    setPagination((prev: InventoryPaginationState) => ({
      ...prev,
      currentPage: 1,
    }));
    setShouldSearch((prev) => !prev);
  };

  const handlePageChange = (newPage: number) => {
    setPagination((prev: InventoryPaginationState) => ({
      ...prev,
      currentPage: newPage,
    }));
  };

  const handleItemsPerPageChange = (event: SelectChangeEvent<unknown>) => {
    setPagination((prev: InventoryPaginationState) => ({
      ...prev,
      itemsPerPage: parseInt(event.target.value as string),
      currentPage: 1,
    }));
  };

  const handleTableSortChange = (field: 'createdAt' | 'recordCompletedAt' | 'recordCode') => {
    setTableSort((prev) => {
      if (prev.sortBy === field) {
        return {
          sortBy: field,
          sortOrder: prev.sortOrder === 'asc' ? 'desc' : 'asc',
        };
      }
      return {
        sortBy: field,
        sortOrder: 'asc',
      };
    });
    setPagination((prev) => ({
      ...prev,
      currentPage: 1,
    }));
  };

  // Add panel handlers
  const handleAddRecord = () => {
    setIsEditMode(false);
    setCurrentEditRecord(null);
    setShowAddPanel(true);
  };

  const handleCloseAddPanel = () => {
    setShowAddPanel(false);
    setIsEditMode(false);
    setCurrentEditRecord(null);
  };

  const handleSaveRecord = async (recordData: any) => {
    try {
      if (isEditMode && currentEditRecord) {
        // EDIT MODE: Update existing record with items in a single operation
        // Check if we have individual units data (new format) or basic items (old format)
        const hasIndividualUnits = recordData.items?.some(
          (item: any) => !item.delete && item.individualUnits,
        );

        if (hasIndividualUnits) {
          // Use the new format with individual units
          // For updates, don't include recordCode (backend doesn't allow it)
          const updatePayload = {
            destinationLocationId: parseInt(recordData.destinationLocationId, 10),
            supplierId:
              recordData.supplierId != null && recordData.supplierId !== ''
                ? parseInt(String(recordData.supplierId), 10)
                : undefined,
            status: recordData.status || 'PENDING',
            items: recordData.items?.map((item: any) => {
              if (item.delete) {
                return { id: Number(item.id), delete: true };
              }
              return {
                id:
                  item.id != null && item.id !== '' && Number(item.id) > 0
                    ? Number(item.id)
                    : undefined,
                productId: parseInt(String(item.productId), 10),
                quantity: parseInt(String(item.quantity), 10),
                individualUnits: item.individualUnits,
              };
            }),
          };
          // Use the new endpoint that handles the entire update in one call
          await InventoryRecordsService.inventoryRecordsControllerUpdateWithItems(
            currentEditRecord.id.toString(),
            updatePayload,
          );

          setNotification({
            open: true,
            ...INVENTORY_MESSAGES.RECORD_UPDATED_WITH_ITEMS(updatePayload.items.length),
          });
        } else {
          // Fallback to old format without individual units
          const updatePayload = {
            destinationLocationId: parseInt(recordData.destinationLocationId, 10),
            supplierId:
              recordData.supplierId != null && recordData.supplierId !== ''
                ? parseInt(String(recordData.supplierId), 10)
                : undefined,
            status: recordData.status || 'PENDING',
            items: recordData.items?.map((item: any) => {
              if (item.delete) {
                return { id: Number(item.id), delete: true };
              }
              return {
                id:
                  item.id != null && item.id !== '' && Number(item.id) > 0
                    ? Number(item.id)
                    : undefined,
                productId: parseInt(String(item.productId), 10),
                quantity: parseInt(String(item.quantity), 10),
              };
            }),
          };

          // Use the new endpoint that handles the entire update in one call
          await InventoryRecordsService.inventoryRecordsControllerUpdateWithItems(
            currentEditRecord.id.toString(),
            updatePayload,
          );

          setNotification({
            open: true,
            ...INVENTORY_MESSAGES.RECORD_UPDATED(updatePayload.items?.length ?? 0),
          });
        }
      } else if (!isEditMode) {
        // CREATE MODE: Create new record with items in a single operation
        // Check if we have individual units data (new format) or basic items (old format)
        const hasIndividualUnits = recordData.items?.some((item: any) => item.individualUnits);

        if (hasIndividualUnits) {
          // Use the new format with individual units
          const createPayload = {
            destinationLocationId: parseInt(recordData.destinationLocationId, 10),
            supplierId:
              recordData.supplierId != null && recordData.supplierId !== ''
                ? parseInt(String(recordData.supplierId), 10)
                : undefined,
            status: recordData.status || 'PENDING',
            items: recordData.items?.map((item: any) => ({
              productId: parseInt(item.productId),
              quantity: parseInt(item.quantity),
              individualUnits: item.individualUnits,
            })),
          };

          // Use the new endpoint that handles the entire creation in one call
          const createdRecord =
            await InventoryRecordsService.inventoryRecordsControllerCreateWithItems(createPayload);

          // eslint-disable-next-line no-console
          console.log(
            'Successfully created inventory record with individual units:',
            createdRecord,
          );

          setNotification({
            open: true,
            ...INVENTORY_MESSAGES.RECORD_CREATED_WITH_ITEMS(createPayload.items.length),
          });
        } else {
          // Fallback to old format without individual units
          const createPayload = {
            destinationLocationId: parseInt(recordData.destinationLocationId, 10),
            supplierId:
              recordData.supplierId != null && recordData.supplierId !== ''
                ? parseInt(String(recordData.supplierId), 10)
                : undefined,
            status: recordData.status || 'PENDING',
            items: recordData.items?.map((item: any) => ({
              productId: parseInt(item.productId),
              quantity: parseInt(item.quantity),
            })),
          };

          // Use the new endpoint that handles the entire creation in one call
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const createdRecord =
            await InventoryRecordsService.inventoryRecordsControllerCreateWithItems(createPayload);

          setNotification({
            open: true,
            ...INVENTORY_MESSAGES.RECORD_CREATED(createPayload.items.length),
          });
        }
      }

      // Close the add panel
      setShowAddPanel(false);
      setIsEditMode(false);
      setCurrentEditRecord(null);

      // Refetch inventory records to update the list
      fetchInventory();
      void fetchStatusSummary();
    } catch (error: any) {
      console.error(`Error ${isEditMode ? 'updating' : 'creating'} inventory record:`, error);

      // Use helper function to create error notification
      const defaultMessage = isEditMode
        ? INVENTORY_MESSAGES.UPDATE_ERROR.message
        : INVENTORY_MESSAGES.CREATE_ERROR.message;

      setNotification({
        open: true,
        ...createErrorNotification(defaultMessage, error),
      });
      // Re-throw so AddInventoryPanel can keep the confirm modal open and unlock submit
      throw error;
    }
  };

  // Import/Export handlers
  const handleImport = () => {
    setNotification({
      open: true,
      ...INVENTORY_MESSAGES.IMPORT_NOT_IMPLEMENTED,
    });
  };

  const handleExport = async () => {
    setIsExporting(true);
    setNotification({ open: true, ...INVENTORY_MESSAGES.EXPORTING });

    try {
      // Same filter mapping as fetchInventory, so the export always matches
      // what's currently on screen (applied filters).
      let statusParam: 'COMPLETED' | 'CANCELLED' | 'PENDING' | undefined;
      if (appliedFilters.recordStatus) {
        const statusLower = appliedFilters.recordStatus.toLowerCase();
        if (statusLower === 'completed' || statusLower === 'completado') {
          statusParam = 'COMPLETED';
        } else if (statusLower === 'cancelled' || statusLower === 'cancelado') {
          statusParam = 'CANCELLED';
        } else if (statusLower === 'pending' || statusLower === 'pendiente') {
          statusParam = 'PENDING';
        }
      }

      await downloadCsvExport(
        '/inventory-records/export/csv',
        {
          recordCode: appliedFilters.productCode || undefined,
          status: statusParam,
          startDate: appliedFilters.startDate || undefined,
          endDate: appliedFilters.endDate || undefined,
          searchTerm: appliedFilters.productName || undefined,
          supplierId: appliedFilters.supplierId
            ? parseInt(appliedFilters.supplierId, 10)
            : undefined,
        },
        `registros-inventario-${csvExportDateSuffix()}.csv`,
      );

      setNotification({ open: true, ...INVENTORY_MESSAGES.EXPORTED_SUCCESS });
    } catch (error) {
      console.error('Error exporting inventory records:', error);
      const errorMessage = error instanceof CsvExportError ? error.message : undefined;
      setNotification({ open: true, ...INVENTORY_MESSAGES.EXPORT_ERROR(errorMessage) });
    } finally {
      setIsExporting(false);
    }
  };

  // Record action handlers — view uses paginated /detailed-items; edit/print keep full /detailed
  const handleViewRecord = async (id: number) => {
    try {
      const normalizedRecord = await fetchRecordPage(id, 1, itemsPerPage);

      if (normalizedRecord != null) {
        setCurrentViewRecord(normalizedRecord);
        setItemsPage(1);
        setExpandedProducts(new Set());
        setShowViewPanel(true);
      } else {
        throw new Error('Failed to fetch record details');
      }
    } catch (error) {
      console.error('Error fetching record for view:', error);
      setNotification({
        open: true,
        ...INVENTORY_MESSAGES.LOAD_DETAILS_ERROR,
      });
    }
  };

  const handleViewItemsPageChange = async (page: number) => {
    if (currentViewRecord == null || page === itemsPage) return;

    setItemsPage(page);
    setIsViewItemsLoading(true);
    try {
      const nextPage = await fetchRecordPage(currentViewRecord.id, page, itemsPerPage);
      if (nextPage != null) {
        setCurrentViewRecord(nextPage);
        setExpandedProducts(new Set());
      } else {
        setNotification({
          open: true,
          ...INVENTORY_MESSAGES.LOAD_DETAILS_ERROR,
        });
      }
    } catch (error) {
      console.error('Error fetching record detail page:', error);
      setNotification({
        open: true,
        ...INVENTORY_MESSAGES.LOAD_DETAILS_ERROR,
      });
    } finally {
      setIsViewItemsLoading(false);
    }
  };

  const handleEditRecord = async (id: number) => {
    // Find the record in the current data
    const record = inventoryRecords.find((r) => r.id === id);

    if (record) {
      if (isInventoryRecordCompleted(record.status)) {
        setNotification({
          open: true,
          ...INVENTORY_MESSAGES.CANNOT_EDIT_COMPLETED,
        });
        return;
      }
      if (isInventoryRecordCancelled(record.status)) {
        setNotification({
          open: true,
          message: 'No se puede editar un registro cancelado.',
          severity: 'warning',
        });
        return;
      }

      try {
        // Use our custom hook to fetch record details with normalized data format
        const normalizedRecord = await fetchInventoryRecordDetails(id);

        if (normalizedRecord) {
          // Set the current record for editing with properly formatted data
          setCurrentEditRecord(normalizedRecord);

          // Set edit mode
          setIsEditMode(true);

          // Open the panel
          setShowAddPanel(true);
        } else {
          throw new Error('Failed to fetch record details');
        }
      } catch (error) {
        console.error('Error fetching record for edit:', error);
        setNotification({
          open: true,
          ...INVENTORY_MESSAGES.LOAD_DETAILS_EDIT_ERROR,
        });
      }
    }
  };

  const handleCancelRecord = async (id: number) => {
    // Find the record to get its code
    const record = inventoryRecords.find((r) => r.id === id);
    if (!record) return;

    if (isInventoryRecordCancelled(record.status)) {
      setNotification({
        open: true,
        message: 'Este registro ya está cancelado.',
        severity: 'info',
      });
      return;
    }

    setCancelModal({
      open: true,
      recordId: id,
      recordCode: record.recordCode ?? '',
      loading: false,
    });
  };

  const handleConfirmCancel = async () => {
    if (!cancelModal.recordId) return;

    setCancelModal((prev) => ({ ...prev, loading: true }));

    try {
      // Use the cancel API endpoint
      await InventoryRecordsService.inventoryRecordsControllerCancelRecord(
        cancelModal.recordId.toString(),
      );

      setNotification({
        open: true,
        ...INVENTORY_MESSAGES.RECORD_CANCELED_SUCCESS,
      });

      // Close modal
      setCancelModal({
        open: false,
        recordId: null,
        recordCode: '',
        loading: false,
      });

      // Refetch inventory records to update the list
      fetchInventory();
      void fetchStatusSummary();
    } catch (error: any) {
      console.error('Error cancelling inventory record:', error);
      setNotification({
        open: true,
        ...createErrorNotification('Error al cancelar el registro', error),
      });

      setCancelModal((prev) => ({ ...prev, loading: false }));
    }
  };

  const handleCloseCancelModal = () => {
    if (!cancelModal.loading) {
      setCancelModal({
        open: false,
        recordId: null,
        recordCode: '',
        loading: false,
      });
    }
  };

  const handleCloseNotification = () => {
    setNotification((prev) => ({ ...prev, open: false }));
  };

  const handleCloseViewPanel = () => {
    setShowViewPanel(false);
    setCurrentViewRecord(null);
    setExpandedProducts(new Set()); // Reset expanded products when closing
    setItemsPage(1); // Reset pagination when closing
  };

  // Function to toggle product line expansion (by inventory record item id)
  const toggleProductExpansion = (itemId: number) => {
    setExpandedProducts((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  };

  const warehouseProductsByItemId = useMemo(() => {
    const map = new Map<number, WarehouseProduct[]>();
    if (!currentViewRecord?.warehouseProducts) {
      return map;
    }

    for (const wp of currentViewRecord.warehouseProducts) {
      const itemId = wp.inventoryRecordItemId;
      if (itemId == null || !Number.isFinite(Number(itemId))) {
        continue;
      }
      const key = Number(itemId);
      if (!map.has(key)) {
        map.set(key, []);
      }
      map.get(key)!.push(wp);
    }

    for (const [itemId, products] of map) {
      map.set(itemId, sortByProductCodeUnique(products));
    }

    return map;
  }, [currentViewRecord?.warehouseProducts]);

  /** Fallback when API omits inventoryRecordItemId (legacy responses). */
  const warehouseProductsByProductCode = useMemo(() => {
    const map = new Map<string, WarehouseProduct[]>();
    if (!currentViewRecord?.warehouseProducts) {
      return map;
    }

    for (const wp of currentViewRecord.warehouseProducts) {
      if (wp.inventoryRecordItemId != null) {
        continue;
      }
      const code = wp.productCode;
      if (!map.has(code)) {
        map.set(code, []);
      }
      map.get(code)!.push(wp);
    }

    for (const [code, products] of map) {
      map.set(code, sortByProductCodeUnique(products));
    }

    return map;
  }, [currentViewRecord?.warehouseProducts]);

  const getWarehouseProductsForItem = (item: {
    id?: number | string;
    product?: { code?: string };
  }) => {
    const rawId = item.id;
    const itemId =
      typeof rawId === 'number'
        ? rawId
        : rawId != null && String(rawId).trim() !== ''
          ? Number(rawId)
          : NaN;
    if (Number.isFinite(itemId) && warehouseProductsByItemId.has(itemId)) {
      return warehouseProductsByItemId.get(itemId) ?? [];
    }
    const code = item.product?.code;
    if (code == null) {
      return [];
    }
    return warehouseProductsByProductCode.get(code) ?? [];
  };
  // Server already returns the current page of product lines
  const currentPageItems = useMemo(() => {
    return currentViewRecord?.items ?? [];
  }, [currentViewRecord?.items]);

  const viewItemsPagination = currentViewRecord?.itemsPagination;
  const viewItemsTotal = viewItemsPagination?.total ?? currentPageItems.length;
  const viewItemsTotalPages =
    viewItemsPagination?.totalPages ??
    (viewItemsTotal > 0 ? Math.ceil(viewItemsTotal / itemsPerPage) : 0);
  const viewPageStartIndex = (itemsPage - 1) * itemsPerPage;

  const currentPageExpandableItemIds = useMemo(() => {
    const ids: number[] = [];
    for (const item of currentPageItems) {
      const rawId = item.id;
      const itemId =
        typeof rawId === 'number'
          ? rawId
          : rawId != null && String(rawId).trim() !== ''
            ? Number(rawId)
            : NaN;
      if (!Number.isFinite(itemId)) continue;
      const warehouseProducts = getWarehouseProductsForItem(item);
      if (resolvePieceDetailsForItem(item, warehouseProducts).length > 0) {
        ids.push(itemId);
      }
    }
    return ids;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPageItems, warehouseProductsByItemId, warehouseProductsByProductCode]);

  const isCurrentPageFullyExpanded =
    currentPageExpandableItemIds.length > 0 &&
    currentPageExpandableItemIds.every((id) => expandedProducts.has(id));

  const handleToggleExpandCurrentPage = () => {
    setExpandedProducts((prev) => {
      const next = new Set(prev);
      if (isCurrentPageFullyExpanded) {
        currentPageExpandableItemIds.forEach((id) => next.delete(id));
      } else {
        currentPageExpandableItemIds.forEach((id) => next.add(id));
      }
      return next;
    });
  };

  const viewRecordPricingTotals = useMemo(() => {
    if (currentViewRecord?.status !== 'COMPLETED') return null;
    return currentViewRecord.pricingTotals ?? null;
  }, [currentViewRecord]);

  const viewRecordTotalUnits = currentViewRecord?.totalItemsCount ?? 0;

  // Print handler for inventory record from table
  const handleOpenLabelPrint = (id: number) => {
    const rec = inventoryRecords.find((r) => r.id === id);
    if (!isInventoryRecordCompleted(rec?.status)) {
      setNotification({
        open: true,
        message: 'Solo se pueden imprimir etiquetas de registros completados.',
        severity: 'info',
      });
      return;
    }
    setLabelPrintModal({
      open: true,
      recordId: id,
      recordCode: rec?.recordCode ?? '',
    });
  };

  const handleCloseLabelPrint = () => {
    setLabelPrintModal({ open: false, recordId: null, recordCode: '' });
  };

  const handlePrintRecordFromTable = async (id: number) => {
    const rec = inventoryRecords.find((r) => r.id === id);
    if (isInventoryRecordCancelled(rec?.status)) {
      setNotification({
        open: true,
        message: 'No se puede imprimir un registro cancelado.',
        severity: 'info',
      });
      return;
    }

    try {
      // Fetch the detailed record
      const normalizedRecord = await fetchInventoryRecordDetails(id);

      if (normalizedRecord) {
        // Use the shared print utility
        printInventoryRecord(normalizedRecord);
      } else {
        setNotification({
          open: true,
          ...INVENTORY_MESSAGES.LOAD_DETAILS_PRINT_ERROR,
        });
      }
    } catch (error) {
      console.error('Error fetching record for print:', error);
      setNotification({
        open: true,
        message: 'Error al cargar los detalles del registro para imprimir',
        severity: 'error',
      });
    }
  };

  // Calculate pagination values (commented out as not used in current implementation)
  // const totalPages = Math.ceil(pagination.totalItems / pagination.itemsPerPage) || 1;
  // const startItem = pagination.totalItems > 0 ? (pagination.currentPage - 1) * pagination.itemsPerPage + 1 : 0;
  // const endItem = Math.min(pagination.currentPage * pagination.itemsPerPage, pagination.totalItems);

  const pageContent = (
    <ListPageScaffold>
      <ListPageMain sectionGap={1}>
        <ListPageToolbar>
          <ActionButtons
            onAddRecord={handleAddRecord}
            onImport={handleImport}
            onExport={handleExport}
            showAdd={canAdd}
            showExport={canExport}
            exportDisabled={isExporting}
            exportLabel={isExporting ? 'Exportando...' : 'Exportar'}
          />
        </ListPageToolbar>

        <StatusSummaryBar
          metrics={INVENTORY_RECORD_SUMMARY_METRICS}
          counts={{
            total: statusSummary.total,
            completed: statusSummary.completed,
            pending: statusSummary.pending,
            cancelled: statusSummary.cancelled,
          }}
          loading={statusSummaryLoading}
          activeFilterValue={appliedFilters.recordStatus}
          onMetricClick={handleStatusMetricClick}
        />

        <ListPageFiltersSection>
          <InventoryFilters
            filters={filters}
            setFilters={setFilters}
            appliedFilters={appliedFilters}
            onClearAppliedField={handleClearAppliedField}
            onSearch={handleSearch}
            onReset={handleReset}
            suppliers={suppliersList}
          />
        </ListPageFiltersSection>

        {/* Inventory records table - Opción 4: Sin Paper, borde y fondo sutil */}
        {!isLoading && inventoryRecords.length > 0 && (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              borderRadius: 0,
              border: 'none',
              bgcolor: 'transparent',
              overflow: 'visible',
              minHeight: 0,
              px: 0,
            }}
          >
            <InventoryTable
              records={inventoryRecords}
              loading={isLoading}
              selectedRecords={selectedRecords}
              setSelectedRecords={setSelectedRecords}
              onViewRecord={canViewRow ? handleViewRecord : undefined}
              onEditRecord={canEdit ? handleEditRecord : undefined}
              onCancelRecord={canCancel ? handleCancelRecord : undefined}
              onPrintRecord={canPrintDetail ? handlePrintRecordFromTable : undefined}
              onPrintLabels={canLabelPrint ? handleOpenLabelPrint : undefined}
              sortBy={tableSort.sortBy}
              sortOrder={tableSort.sortOrder}
              onSortChange={handleTableSortChange}
            />
          </Box>
        )}

        {/* Loading state */}
        {isLoading && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              p: 4,
              minHeight: '200px',
            }}
          >
            <CircularProgress />
          </Box>
        )}

        {/* Empty state (también tras fallo de red/API: mismo mensaje neutro, sin alerta roja) */}
        {!isLoading && inventoryRecords.length === 0 && (
          <EmptyStateMessage
            message="No hay registros de inventario para mostrar."
            minHeight={200}
          />
        )}

        {/* Pagination - Opción 4: Sin Paper, sin borde superior */}
        {!isLoading && inventoryRecords.length > 0 && (
          <Box>
            <CommonPagination
              currentPage={pagination.currentPage}
              itemsPerPage={pagination.itemsPerPage}
              totalItems={pagination.totalItems}
              onPageChange={handlePageChange}
              onItemsPerPageChange={handleItemsPerPageChange}
            />
          </Box>
        )}
      </ListPageMain>

      {/* Add Inventory Panel */}
      <AddInventoryPanel
        open={showAddPanel}
        onClose={handleCloseAddPanel}
        onSave={handleSaveRecord}
        editMode={isEditMode}
        recordToEdit={currentEditRecord}
      />

      <InventoryLabelPrintModal
        open={labelPrintModal.open}
        onClose={handleCloseLabelPrint}
        recordId={labelPrintModal.recordId}
        recordCode={labelPrintModal.recordCode}
      />

      {/* Cancel Confirmation Modal */}
      <CancelConfirmationModal
        open={cancelModal.open}
        onClose={handleCloseCancelModal}
        onConfirm={handleConfirmCancel}
        recordCode={cancelModal.recordCode}
        loading={cancelModal.loading}
      />

      {/* Notification Snackbar */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
      {/* View Record Panel - Convertido a Dialog de MUI */}
      <StyledDialog
        open={showViewPanel}
        onClose={handleCloseViewPanel}
        maxWidth="md"
        fullWidth
        // Más ancho que los demás modales de detalle: la tabla de piezas tiene 5 columnas.
        sx={{ '& .MuiDialog-paper': { maxWidth: 900, maxHeight: '85vh' } }}
      >
        <StyledDialogTitle>
          <Typography variant="h6" component="span">
            Detalles del Registro de Inventario
          </Typography>
          <CloseButton onClick={handleCloseViewPanel} aria-label="Cerrar panel">
            <CloseIcon />
          </CloseButton>
        </StyledDialogTitle>

        {/* Content - Printable area */}
        <StyledDialogContent>
          <Box id="inventory-record-content">
            {/* Registro - Info Card */}
            <Box sx={(theme) => ({ ...insetPanelPaperSx(theme), mb: 3 })}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  mb: 2,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{ color: 'text.secondary', fontWeight: 500, display: 'block' }}
                    >
                      Número de Registro de Inventario
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary' }}>
                      {currentViewRecord?.recordCode}
                    </Typography>
                  </Box>
                </Box>
                <Chip
                  label={
                    currentViewRecord?.status === 'COMPLETED'
                      ? 'Completado'
                      : currentViewRecord?.status === 'CANCELLED'
                        ? 'Cancelado'
                        : currentViewRecord?.status === 'PENDING'
                          ? 'Pendiente'
                          : currentViewRecord?.status
                  }
                  sx={{
                    px: 1.5,
                    py: 0.5,
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    ...(currentViewRecord?.status?.toLowerCase() === 'completed' ||
                    currentViewRecord?.status === 'Completado'
                      ? {
                          bgcolor: colors.success.light + '20',
                          color: colors.success.dark,
                        }
                      : currentViewRecord?.status?.toLowerCase() === 'cancelled' ||
                          currentViewRecord?.status === 'Cancelado'
                        ? {
                            bgcolor: colors.error.light + '20',
                            color: colors.error.dark,
                          }
                        : {
                            bgcolor: colors.info.light + '20',
                            color: colors.info.dark,
                          }),
                  }}
                />
              </Box>

              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                      <Typography
                        variant="caption"
                        sx={{ color: 'text.secondary', fontWeight: 500 }}
                      >
                        Ubicación
                      </Typography>
                    </Box>
                    <Typography variant="body1" sx={{ fontWeight: 600, color: 'text.primary' }}>
                      {currentViewRecord?.location}
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                      <Typography
                        variant="caption"
                        sx={{ color: 'text.secondary', fontWeight: 500 }}
                      >
                        Fecha de creación
                      </Typography>
                    </Box>
                    <Typography variant="body1" sx={{ fontWeight: 600, color: 'text.primary' }}>
                      {currentViewRecord?.createdAt
                        ? formatDateTime(currentViewRecord.createdAt) || '—'
                        : '—'}
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                      <Typography
                        variant="caption"
                        sx={{ color: 'text.secondary', fontWeight: 500 }}
                      >
                        Fecha de registro
                      </Typography>
                    </Box>
                    <Typography variant="body1" sx={{ fontWeight: 600, color: 'text.primary' }}>
                      {currentViewRecord?.completedAt
                        ? formatDateTime(currentViewRecord.completedAt) || '—'
                        : '—'}
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                      <Typography
                        variant="caption"
                        sx={{ color: 'text.secondary', fontWeight: 500 }}
                      >
                        Registrado por
                      </Typography>
                    </Box>
                    <Typography variant="body1" sx={{ fontWeight: 600, color: 'text.primary' }}>
                      {currentViewRecord?.registeredBy?.fullName?.trim() ||
                        currentViewRecord?.registeredBy?.username?.trim() ||
                        '—'}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>

            {/* Lista de Productos con Detalles Expandibles */}
            <Box
              sx={{
                bgcolor: 'background.paper',
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'divider',
                overflow: 'hidden',
              }}
            >
              <Box
                sx={{
                  px: 3,
                  py: 1.5,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 2,
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                  Productos ({viewItemsTotal})
                </Typography>
                {currentPageExpandableItemIds.length > 0 && (
                  <Button
                    size="small"
                    onClick={handleToggleExpandCurrentPage}
                    sx={{ textTransform: 'none', fontWeight: 500 }}
                  >
                    {isCurrentPageFullyExpanded ? 'Ocultar todo' : 'Mostrar todo'}
                  </Button>
                )}
              </Box>

              {currentViewRecord?.items && currentViewRecord.items.length > 0 ? (
                <Box sx={{ position: 'relative', opacity: isViewItemsLoading ? 0.55 : 1 }}>
                  {(() => {
                    const startIndex = viewPageStartIndex;
                    const endIndex = startIndex + currentPageItems.length;
                    const paginatedItems = currentPageItems;
                    const totalItemsPages = viewItemsTotalPages;

                    return (
                      <>
                        {paginatedItems.map((item, relativeIndex) => {
                          const index = startIndex + relativeIndex;
                          const warehouseProducts = getWarehouseProductsForItem(item);
                          const pieceDetails = resolvePieceDetailsForItem(item, warehouseProducts);
                          const rawItemId = item.id;
                          const lineItemId =
                            typeof rawItemId === 'number'
                              ? rawItemId
                              : rawItemId != null && String(rawItemId).trim() !== ''
                                ? Number(rawItemId)
                                : NaN;
                          const isExpanded = Number.isFinite(lineItemId)
                            ? expandedProducts.has(lineItemId)
                            : false;
                          const hasPieceDetails = pieceDetails.length > 0;

                          return (
                            <Box
                              key={item.id || index}
                              sx={{
                                transition: 'all 0.2s',
                                borderBottom:
                                  relativeIndex < paginatedItems.length - 1 ? '1px solid' : 'none',
                                borderColor: 'divider',
                              }}
                            >
                              {/* Producto Principal */}
                              <Box
                                sx={{
                                  px: 3,
                                  py: 1.5,
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                  '&:hover': {
                                    bgcolor: 'action.hover',
                                  },
                                  transition: 'background-color 0.2s',
                                }}
                              >
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                  <Box>
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        fontWeight: 500,
                                        color: 'text.primary',
                                        display: 'block',
                                      }}
                                    >
                                      {item.product?.name || `Producto ${item.productId}`}
                                    </Typography>
                                    <Typography
                                      variant="caption"
                                      sx={{ color: 'text.secondary', display: 'block' }}
                                    >
                                      {item.product?.code || '-'}
                                    </Typography>
                                  </Box>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                  <Chip
                                    label={`${item.quantity} pza.`}
                                    size="small"
                                    sx={{
                                      bgcolor: 'action.selected',
                                      color: 'text.primary',
                                      fontWeight: 600,
                                      fontSize: '0.875rem',
                                    }}
                                  />
                                  {hasPieceDetails ? (
                                    <Button
                                      onClick={() =>
                                        Number.isFinite(lineItemId) &&
                                        toggleProductExpansion(lineItemId)
                                      }
                                      size="small"
                                      sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 0.5,
                                        color: 'text.secondary',
                                        textTransform: 'none',
                                        px: 1,
                                        py: 0.5,
                                        '&:hover': {
                                          color: 'text.primary',
                                          bgcolor: 'action.hover',
                                        },
                                        transition: 'all 0.2s',
                                      }}
                                      title={
                                        isExpanded
                                          ? 'Ocultar detalles de piezas'
                                          : 'Ver detalles de piezas'
                                      }
                                    >
                                      <Typography variant="caption" sx={{ fontWeight: 500 }}>
                                        {isExpanded ? 'Ocultar' : 'Ver'} detalles
                                      </Typography>
                                      <Box
                                        component="svg"
                                        xmlns="http://www.w3.org/2000/svg"
                                        sx={{
                                          width: '1rem',
                                          height: '1rem',
                                          transition: 'transform 0.2s',
                                          transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                        }}
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                      >
                                        <path
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                          strokeWidth={2}
                                          d="M19 9l-7 7-7-7"
                                        />
                                      </Box>
                                    </Button>
                                  ) : (
                                    <Typography
                                      variant="caption"
                                      sx={{ color: 'text.disabled', px: 1, py: 0.5 }}
                                    >
                                      Sin detalles de piezas
                                    </Typography>
                                  )}
                                </Box>
                              </Box>

                              {/* Detalles Expandibles */}
                              {isExpanded && hasPieceDetails && (
                                <Box
                                  sx={{
                                    borderTop: '1px solid',
                                    borderColor: 'divider',
                                    px: 3,
                                    py: 2,
                                  }}
                                >
                                  <Typography
                                    variant="subtitle2"
                                    sx={{ fontWeight: 600, color: 'text.primary', mb: 1.5 }}
                                  >
                                    Detalles de piezas ({pieceDetails.length})
                                  </Typography>

                                  <TableContainer
                                    component={Box}
                                    sx={{
                                      bgcolor: 'background.paper',
                                      border: '1px solid',
                                      borderColor: 'divider',
                                      borderRadius: 1,
                                    }}
                                  >
                                    <Table size="small">
                                      <TableHead>
                                        <TableRow>
                                          <TableCell
                                            sx={{
                                              fontWeight: 600,
                                              fontSize: '0.75rem',
                                              textTransform: 'uppercase',
                                              color: 'text.secondary',
                                            }}
                                          >
                                            Código Único
                                          </TableCell>
                                          <TableCell
                                            sx={{
                                              fontWeight: 600,
                                              fontSize: '0.75rem',
                                              textTransform: 'uppercase',
                                              color: 'text.secondary',
                                            }}
                                          >
                                            Nombre del Producto
                                          </TableCell>
                                          <TableCell
                                            sx={{
                                              fontWeight: 600,
                                              fontSize: '0.75rem',
                                              textTransform: 'uppercase',
                                              color: 'text.secondary',
                                            }}
                                          >
                                            Tipo de Producto
                                          </TableCell>
                                          <TableCell
                                            sx={{
                                              fontWeight: 600,
                                              fontSize: '0.75rem',
                                              textTransform: 'uppercase',
                                              color: 'text.secondary',
                                            }}
                                          >
                                            Estado
                                          </TableCell>
                                          <TableCell
                                            sx={{
                                              fontWeight: 600,
                                              fontSize: '0.75rem',
                                              textTransform: 'uppercase',
                                              color: 'text.secondary',
                                            }}
                                          >
                                            Material
                                          </TableCell>
                                        </TableRow>
                                      </TableHead>
                                      <TableBody>
                                        {pieceDetails.map((detail, detailIndex) => (
                                          <TableRow key={detail.key || detailIndex} hover>
                                            <TableCell
                                              sx={{
                                                fontWeight: 500,
                                                color: 'text.primary',
                                                whiteSpace: 'nowrap',
                                              }}
                                            >
                                              {detail.productCodeUnique}
                                            </TableCell>
                                            <TableCell
                                              sx={{ color: 'text.primary', whiteSpace: 'nowrap' }}
                                            >
                                              {detail.productName}
                                            </TableCell>
                                            <TableCell
                                              sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}
                                            >
                                              {detail.productType}
                                            </TableCell>
                                            <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                              <Chip
                                                label={
                                                  detail.status === 'ASSIGNED'
                                                    ? INV_STATUS_BRANCH_LABEL
                                                    : detail.status === 'IN_TRANSIT'
                                                      ? 'En Tránsito'
                                                      : detail.status === 'UNASSIGNED'
                                                        ? INV_STATUS_WAREHOUSE_LABEL
                                                        : detail.status === 'PENDING'
                                                          ? 'Pendiente'
                                                          : detail.status === 'CANCELLED'
                                                            ? 'Cancelado'
                                                            : detail.status
                                                }
                                                size="small"
                                                sx={{
                                                  px: 1,
                                                  py: 0.5,
                                                  fontSize: '0.75rem',
                                                  fontWeight: 600,
                                                  ...(detail.status === 'ASSIGNED'
                                                    ? {
                                                        bgcolor: 'success.100',
                                                        color: 'success.800',
                                                      }
                                                    : detail.status === 'IN_TRANSIT'
                                                      ? {
                                                          bgcolor: 'primary.100',
                                                          color: 'primary.800',
                                                        }
                                                      : detail.status === 'UNASSIGNED'
                                                        ? {
                                                            bgcolor: 'action.selected',
                                                            color: 'text.primary',
                                                          }
                                                        : detail.status === 'CANCELLED'
                                                          ? {
                                                              bgcolor: 'error.100',
                                                              color: 'error.800',
                                                            }
                                                          : {
                                                              bgcolor: 'warning.100',
                                                              color: 'warning.800',
                                                            }),
                                                }}
                                              />
                                            </TableCell>
                                            <TableCell
                                              sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}
                                            >
                                              {detail.materialLabel}
                                            </TableCell>
                                          </TableRow>
                                        ))}
                                      </TableBody>
                                    </Table>
                                  </TableContainer>
                                </Box>
                              )}
                            </Box>
                          );
                        })}
                        {/* Pagination Controls */}
                        {totalItemsPages > 1 && (
                          <Box
                            sx={{
                              display: 'flex',
                              width: '100%',
                              alignItems: 'center',
                              justifyContent: 'flex-end',
                              flexWrap: 'wrap',
                              gap: 2,
                              py: 2,
                              borderTop: '1px solid',
                              borderColor: 'divider',
                            }}
                          >
                            <Pagination
                              count={totalItemsPages}
                              page={itemsPage}
                              onChange={(_event, page) => {
                                void handleViewItemsPageChange(page);
                              }}
                              color="primary"
                              size="small"
                              showFirstButton
                              showLastButton
                              boundaryCount={3}
                              siblingCount={1}
                              disabled={isViewItemsLoading}
                            />
                            <PaginationGoToPageField
                              currentPage={itemsPage}
                              totalPages={totalItemsPages}
                              onCommit={(page) => {
                                void handleViewItemsPageChange(page);
                              }}
                            />
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                              Mostrando {viewItemsTotal === 0 ? 0 : startIndex + 1}-
                              {Math.min(endIndex, viewItemsTotal)} de {viewItemsTotal} productos
                            </Typography>
                          </Box>
                        )}
                      </>
                    );
                  })()}
                </Box>
              ) : (
                <Box sx={{ p: 3, textAlign: 'center' }}>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    No hay líneas de producto en este registro
                  </Typography>
                </Box>
              )}

              {currentViewRecord?.items && currentViewRecord.items.length > 0 && (
                <Box
                  sx={{
                    px: 3,
                    py: 2,
                    borderTop: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={viewRecordPricingTotals ? 4 : 12}>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          p: 2,
                          bgcolor: 'background.paper',
                          borderRadius: 1.5,
                          border: '1px solid',
                          borderColor: 'divider',
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{ fontWeight: 600, color: 'text.secondary' }}
                        >
                          Total de piezas
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 700, color: 'primary.light' }}
                        >
                          {viewRecordTotalUnits} piezas
                        </Typography>
                      </Box>
                    </Grid>
                    {viewRecordPricingTotals && (
                      <>
                        <Grid item xs={12} sm={4}>
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              p: 2,
                              bgcolor: 'background.paper',
                              borderRadius: 1.5,
                              border: '1px solid',
                              borderColor: 'divider',
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{ fontWeight: 600, color: 'text.secondary' }}
                            >
                              Costo total
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 700, color: '#3b82f6' }}>
                              {formatCurrency(viewRecordPricingTotals.totalCostPrice)}
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              p: 2,
                              bgcolor: 'background.paper',
                              borderRadius: 1.5,
                              border: '1px solid',
                              borderColor: 'divider',
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{ fontWeight: 600, color: 'text.secondary' }}
                            >
                              Precio de Venta Total
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 700, color: '#10b981' }}>
                              {formatCurrency(viewRecordPricingTotals.totalSalePrice)}
                            </Typography>
                          </Box>
                        </Grid>
                      </>
                    )}
                  </Grid>
                </Box>
              )}
            </Box>
          </Box>
        </StyledDialogContent>

        {/* Footer - Botones */}
        <StyledDialogActions>
          <ModernButton variant="tonal" onClick={handleCloseViewPanel}>
            Cerrar
          </ModernButton>
        </StyledDialogActions>
      </StyledDialog>
    </ListPageScaffold>
  );

  return pageContent;
};

export default InventoryPage;
